import { useContext, useState, useRef, useMemo } from "react"
import { Link, useNavigate } from "react-router-dom"
import {
  useQuery,
  useMutation,
  useLazyQuery,
  NetworkStatus,
} from "@apollo/client"
import _ from "lodash"
import { DataTable } from "primereact/datatable"
import { Column } from "primereact/column"
import { LoaderMedium, LoaderSmall } from "../../components/Loaders"
import { SplitButton } from "primereact/splitbutton"
import { Dialog } from "primereact/dialog"
import CustomButton from "../../components/Buttons/CustomButton"
import { classNames } from "primereact/utils"
import FBTSidebar from "../../components/sidebars/FBTSidebar"
import { Messages } from "primereact/messages"
import { LogError, LogInfo } from "../../helpers/logger"

// Context
import { UserContext } from "../../context/userContext"

// queries
import {
  GET_MICROSERVICES,
  GET_BLOCKS_LIBRARY,
  DELETE_CUSTOM_BLOCK,
  GET_TEMPLATES_WITH_BLOCK_ID,
} from "./queries"

import styles from "../../styles/Fields-Library.module.css"

export default function Blocks() {
  const userCtx = useContext(UserContext)
  const errMsg = useRef(null)
  const navigate = useNavigate()

  const [deleteBlock] = useMutation(DELETE_CUSTOM_BLOCK)

  const [assessmentsPlus, setAssessmentsPlus] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [loadingDelete, setLoadingDelete] = useState(false)
  const [executingDelete, setExecutingDelete] = useState(false)
  const [deleteBlockData, setDeleteBlockData] = useState(null)
  const [usedBlocks, setUsedBlocks] = useState([])

  const [getTemplatesWithBlockId] = useLazyQuery(GET_TEMPLATES_WITH_BLOCK_ID)

  const { loading: loadingMicroservices } = useQuery(GET_MICROSERVICES, {
    variables: {
      enterprise_id: userCtx.session.user.enterprise_id,
    },
    onCompleted: (data) => {
      if (data?.microservicesWithAssessement > 0) {
        LogInfo(
          "At least one organization has Assessments+ within this enterprise.",
        )
        return setAssessmentsPlus(true)
      }
      LogInfo("No organization has Assessments+ within this enterprise.")
      return setAssessmentsPlus(false)
    },
    skip: !userCtx,
    fetchPolicy: "network-only",
  })

  const { loading, data, refetch, networkStatus } = useQuery(
    GET_BLOCKS_LIBRARY,
    {
      variables: {
        staff_id: userCtx.session.user.staff_id,
        organization_id: userCtx.session.user.organization_id,
        enterprise_id: userCtx.session.user.enterprise_id,
      },
      skip: !userCtx.session.user,
      notifyOnNetworkStatusChange: true,
      fetchPolicy: "network-only",
      onCompleted: (data) => {
        setUsedBlocks(_.map(data.customBlocks, ({ block_name }) => block_name))
      },
    },
  )

  //  Define the dynamic dialog text fragments
  const deleteConfirmMainMessage = useMemo(() => {
    return deleteBlockData?.isCustomizedSystemBlock ? (
      <>
        Your customization to <b>{deleteBlockData?.block_title}</b> will be
        deleted. The following templates will revert to using the system default
        version…
      </>
    ) : (
      <>
        <b>{deleteBlockData?.block_title}</b> will be deleted. It will be
        removed from the following Templates…
      </>
    )
  }, [deleteBlockData])

  const deleteConfirmDataDisclaimer = useMemo(() => {
    return deleteBlockData?.isCustomizedSystemBlock
      ? "Existing data will remain available in Reports."
      : "No new data will be collected and existing data will remain available in Reports."
  }, [deleteBlockData])

  const isRefreshing = networkStatus === NetworkStatus.refetch
  const isLoading = (loading || loadingMicroservices) && !isRefreshing

  if (isLoading) return <LoaderMedium />
  if (!data) return null

  const handleSysBlocks = (blocks = null) => {
    if (!blocks) return []

    return _.map(blocks, (block) => {
      const handleBehaviorTag = () => {
        const { data_propagation } = block.configuration.behavior
        return data_propagation ? "Update revision" : "Create new"
      }

      const isAssessmentBlock = block.block_name === "session__assessment"

      const handleLink = () => {
        const handleModified = () => {
          return (
            <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
              <span className="material-icons">done</span>Modified
            </div>
          )
        }
        const blockAlreadyInUse = _.includes(usedBlocks, block.block_name)
        const hasAssessmentPlus = assessmentsPlus

        if (isAssessmentBlock) {
          if (!hasAssessmentPlus && blockAlreadyInUse) {
            return handleModified()
          }
          if (hasAssessmentPlus) {
            return (
              <Link
                to={`/block/assessment/add/${block.idBlock}`}
                style={{ display: "flex", alignItems: "center", gap: 5 }}
              >
                <span className="material-icons">add</span>Add
              </Link>
            )
          }
          return (
            <Link
              to={`/block/assessment/customize/${block.idBlock}`}
              style={{ display: "flex", alignItems: "center", gap: 5 }}
            >
              <span className="material-icons">edit</span>Customize
            </Link>
          )
        }
        if (blockAlreadyInUse) {
          return handleModified()
        }
        if (!block.configuration.allow_custom_config) {
          return (
            <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
              <span className="material-icons">lock</span>Locked
            </div>
          )
        }
        return (
          <Link
            to={`/block/customize/${block.idBlock}`}
            style={{ display: "flex", alignItems: "center", gap: 5 }}
          >
            <span className="material-icons">edit</span>Customize
          </Link>
        )
      }
      return {
        ...block,
        customize: handleLink(),
        behavior: handleBehaviorTag(),
      }
    })
  }

  const confirmBlockDeletion = async (block) => {
    setShowDeleteConfirm(true)
    setLoadingDelete(true)

    try {
      // Fetch template data
      const enterprise_id = userCtx.session.user.enterprise_id
      const idBlock = block.idBlock
      const { data: templatesData } = await getTemplatesWithBlockId({
        fetchPolicy: "network-only",
        variables: { idBlock, enterprise_id },
      })

      if (!templatesData || !templatesData.getTemplatesWithBlockId) {
        throw new Error("No data returned for templates with block ID.")
      }

      // Set template data for modal
      setDeleteBlockData({
        idBlock: block.idBlock,
        block_name: block.block_name,
        block_title: block.block_title,
        isCustomizedSystemBlock:
          block.block_parent_path?.startsWith("system-blocks/"),
        templates: templatesData?.getTemplatesWithBlockId
          .map((template) => template.template_title)
          .sort(),
      })
    } catch (error) {
      setShowDeleteConfirm(false) // close the modal
      setDeleteBlockData(null) // clear template data

      LogError("Unable to load template data", error)
      userCtx.setNotificationError(
        "An error occurred while loading template data.",
      )
    } finally {
      setLoadingDelete(false)
    }
  }

  const handleOnDelete = async (block) => {
    try {
      // SOFT Delete the block - TO DO
      setExecutingDelete(true)
      await deleteBlock({
        variables: {
          id: block.idBlock,
        },
      })
      setExecutingDelete(false)
      userCtx.setNotificationInformation("Block deleted successfully.")

      // Close the modal, pause for a moment, then refetch. This creates a better visual effect
      setShowDeleteConfirm(false) // close the modal
      setDeleteBlockData(null) // clear template data
      setTimeout(() => {
        refetch()
      }, 300)
    } catch (error) {
      setExecutingDelete(false)
      LogError("Unable to delete block", error)
      userCtx.setNotificationError(
        "An error occurred while deleting the block.",
      )
    }
  }

  const handleCustomBlocks = (blocks) => {
    if (!blocks) return []
    return _.map(blocks, (block) => {
      const handleBehaviorTag = () => {
        const { data_propagation } = block.configuration.behavior
        return data_propagation ? "Update revision" : "Create new"
      }
      const handleActionBtn = () => {
        const items = [
          {
            label: "Delete",
            icon: "pi pi-trash",
            command: async () => {
              confirmBlockDeletion(block)
            },
          },
        ]
        const handlePath = (idBlock) => {
          const isAssessmentCustomBlock =
            block.block_name === "session__assessment" ||
            block.block_name === "session__assessment____custom"
          if (isAssessmentCustomBlock) {
            return `/block/assessment/edit/${idBlock}`
          }
          return `/block/edit/${idBlock}`
        }
        return (
          <SplitButton
            label="Edit"
            model={items}
            className="mb-2 mr-2 p-button-sm"
            onClick={() => navigate(handlePath(block.idBlock))}
          />
        )
      }
      return {
        ...block,
        action: handleActionBtn(),
        behavior: handleBehaviorTag(),
      }
    })
  }

  return (
    <div className="layout-with-sidebar">
      <div className="sidebar">
        <FBTSidebar />
      </div>
      <div className="main-content">
        <h1 className={styles.title}>
          Library <span>Blocks</span>
        </h1>
        <div className={styles.description}>
          Administrators can update field variables in default blocks or select
          added field variables to modify blocks being used by organizations.
          Blocks need to be customized and updated prior to making any changes
          to application templates.
        </div>

        <h1 className={styles.subTitle}>Default Blocks</h1>

        <div style={{ marginBottom: 30 }}>
          Total Blocks: {data.systemBlocks ? data.systemBlocks.length : 0}
        </div>

        <DataTable
          value={handleSysBlocks(data.systemBlocks || null)}
          className={styles.table}
          sortField="block_library"
          sortOrder={1}
          loading={isRefreshing}
        >
          <Column field="block_library" sortable header="Category" />
          <Column field="block_title" sortable header="Block Name" />
          <Column field="behavior" sortable header="Block Behavior" />
          <Column field="customize" header="Actions" style={{ width: 180 }} />
        </DataTable>

        <h1 className={styles.subTitle}>Custom Blocks</h1>
        <Link to="/block/add" className="btn">
          <div className="flex flex-row gap-2">
            <span className="material-icons">add</span>
            <span>Add New Block</span>
          </div>
        </Link>

        <div style={{ margin: "10px 0" }}>
          <Messages ref={errMsg} />
        </div>

        <div style={{ marginBottom: 30 }}>
          Total Blocks: {data.customBlocks ? data.customBlocks.length : 0}
        </div>

        <DataTable
          value={handleCustomBlocks(data.customBlocks)}
          className={styles.table}
          sortField="block_library"
          sortOrder={1}
          loading={isRefreshing}
        >
          <Column field="block_library" sortable header="Category" />
          <Column field="block_title" sortable header="Block Name" />
          <Column field="behavior" sortable header="Block Behavior" />
          <Column field="action" header="Actions" style={{ width: 180 }} />
        </DataTable>
      </div>

      <Dialog
        header={
          <div className={styles.deleteConfirmHeader}>
            <i
              className={classNames(
                "pi pi-exclamation-triangle",
                styles.deleteConfirmHeaderIcon,
              )}
            />
            <h3>Please confirm</h3>
          </div>
        }
        visible={showDeleteConfirm}
        className={styles.deleteConfirmModal}
        onHide={() => {
          setShowDeleteConfirm(false)
          setDeleteBlockData(null)
        }}
        footer={
          <div>
            <CustomButton
              label="Confirm & Delete"
              onClick={() => handleOnDelete(deleteBlockData)}
              severity="danger"
              size="small"
              disabled={loadingDelete || executingDelete}
              loading={executingDelete}
            />
          </div>
        }
        closable={!executingDelete}
        closeOnEscape={!executingDelete}
      >
        {loadingDelete && (
          <div className={styles.deleteConfirmLoader}>
            <LoaderSmall />
          </div>
        )}

        {!loadingDelete && deleteBlockData && (
          <div>
            {/* Main Message */}
            <div className={styles.deleteConfirmParagraph}>
              {deleteConfirmMainMessage}
            </div>

            {/* Shared Template List */}
            <div className={styles.deleteConfirmParagraph}>
              <ul>
                {deleteBlockData.templates?.length > 0 ? (
                  deleteBlockData.templates.map((template) => (
                    <li key={template}> - {template}</li>
                  ))
                ) : (
                  <li> - None</li>
                )}
              </ul>
            </div>

            {/* Data Disclaimer */}
            <div className={styles.deleteConfirmParagraph}>
              {deleteConfirmDataDisclaimer}
            </div>

            {/* Shared Warning */}
            <div className={styles.deleteConfirmParagraph}>
              <b>This operation is permanent and cannot be reversed.</b>
            </div>
          </div>
        )}
      </Dialog>
    </div>
  )
}
